// src/pages/HomeView/HomeViewPage.tsx
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import './HomeView.css';
import { HomeHeader } from './components/HomeHeader';
import { FilterBar } from '@/components/Filter/FilterBar';
import { SearchBar } from '@/components/Filter/SearchBar';
import { TabNavigator } from './components/TabNavigator';
import { NewsFeedTab } from './components/NewsFeedTab';
import { AssetsTab } from './components/AssetsTab';
import { EfficiencyDataTab } from './components/EfficiencyDataTab';
import { useLicense } from './hooks/useLicense';
import { filterService } from '@/services/filterService';
import { useFilterPersistence } from '@/hooks/useFilterPersistence';
import { useSocketRefreshButton } from '@/hooks/useSocketRefreshButton';
import { RefreshNotificationButton } from '@/components/Dashboard/RefreshNotificationButton';
import type { FilterOption } from '@/types/dashboard';
import { authStorage } from '@/lib';

// Convert FilterOption (from FilterBar) to the API filter format used by sections
const convertFilterOptionToFilter = (opt: FilterOption) => ({
  FilterId: 0,
  FieldName: opt.FieldName,
  Operator: opt.ComparisonOperator,
  Name: opt.FieldName,
  Value: opt.Id,
  Text: opt.Name,
  IsCustomField: opt.IsCustomField,
  FieldTypeId: opt.FieldTypeId,
  CustomFields: opt.CustomFields,
  ComparisonOperator: opt.ComparisonOperator,
  isChipFilter: false,
});

export type HomeTab = 'newsfeed' | 'assets' | 'efficiency';

/**
 * HomeViewContent - Content for the Home page (without layout shell)
 * Used inside MainLayout via React Router's Outlet
 */
export const HomeViewContent: React.FC = () =>
{
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<HomeTab>('newsfeed');

  // Get personId from URL params or use current user's personId
  const urlPersonId = searchParams.get('personId');
  const viewAsPersonId = urlPersonId || authStorage.getPersonId() || '0';
  const isViewingAsOtherUser = !!urlPersonId && urlPersonId !== authStorage.getPersonId();
  const [viewingUserName, setViewingUserName] = useState<string>('');

  // Callback to receive user data from HomeHeader
  const handleUserLoaded = useCallback((userName: string, _imageUrl: string) =>
  {
    setViewingUserName(userName);
  }, []);

  // Filter persistence (UI chips + applied filters + localStorage)
  const {
    filtersSelected,
    appliedFilters,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
  } = useFilterPersistence('C2_Filters_Home');

  // Filter state (UI)
  const [filter, setFilter] = useState('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Search state — persisted to localStorage
  const [searchText, setSearchText] = useState<string>(
    () => localStorage.getItem('C2_SearchText_Home') ?? '',
  );

  useEffect(() =>
  {
    localStorage.setItem('C2_SearchText_Home', searchText);
  }, [searchText]);

  // Socket refresh token for HomeView sections
  const [refreshToken, setRefreshToken] = useState(0);
  const { hasPendingUpdates, pendingCount, triggerRefresh } = useSocketRefreshButton({
    currentDashboardId: null,
    onRefresh: () => setRefreshToken(t => t + 1),
  });

  // Update browser tab title
  useEffect(() =>
  {
    const title = isViewingAsOtherUser && viewingUserName
      ? `Home (${viewingUserName})`
      : 'Home';
    document.title = title;
  }, [isViewingAsOtherUser, viewingUserName]);

  // True once the user has typed in the filter input
  const filterTouchedRef = useRef<boolean>(false);

  // Load filter options when filter text changes (debounced)
  useEffect(() =>
  {
    const loadFilterOptions = async () =>
    {
      if (filter.length > 0) filterTouchedRef.current = true;
      // Below the search threshold, reload the full list instead of clearing it
      const searchTerm = filter.length >= 2 ? filter : null;
      // On first mount the full list is loaded by onFilterOpened instead
      if (!searchTerm && !filterTouchedRef.current) return;

      try
      {
        setFilterLoading(true);
        setFilterError(null);
        const options = await filterService.getFilterOptions(searchTerm, {
          IsFromProject: false,
          ConfigValueId: 1,
        });
        setFilterOptions(options);
      }
      catch (err)
      {
        console.error('Error loading filter options:', err);
        setFilterError('Error loading filters');
      }
      finally
      {
        setFilterLoading(false);
      }
    };

    const debounceTimeout = setTimeout(loadFilterOptions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [filter]);

  // Load initial filter options when FilterIcon is clicked
  const handleFilterOpened = useCallback(async () =>
  {
    try
    {
      setFilterLoading(true);
      setFilterError(null);
      const options = await filterService.getFilterOptions(null, {
        IsFromProject: false,
        ConfigValueId: 1,
      });
      setFilterOptions(options);
    }
    catch (err)
    {
      console.error('Error loading filter options:', err);
      setFilterError('Error loading filters');
    }
    finally
    {
      setFilterLoading(false);
    }
  }, []);

  // Convert applied FilterOptions to API format for child sections (memoized to avoid re-renders)
  const apiFilters = useMemo(
    () => appliedFilters.map(convertFilterOptionToFilter),
    [appliedFilters],
  );

  // Extract boolean/toggle filters (Id "0" or "1") as top-level API params (e.g. ShowCompleted: 1)
  const extraFilterParams = useMemo(() =>
  {
    const extra: Record<string, any> = {};
    appliedFilters.forEach(f =>
    {
      if (f.Id === '0' || f.Id === '1')
      {
        extra[f.FieldName] = parseInt(f.Id, 10);
      }
    });
    extra.IsFilterPersonal = false;
    return extra;
  }, [appliedFilters]);

  const { isLicenseValid, licenseError, isLoading: licenseLoading } = useLicense();

  if (licenseLoading)
  {
    return (
      <div className="home-view-loading flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5dade2] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show license error
  if (!isLicenseValid && licenseError)
  {
    return (
      <div className="home-view-license flex-1 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-2xl w-full text-center">
          <div className="mb-6">
            <img
              src="https://cgbot.corgee.com/services/GifFiles/2.gif"
              alt="License Error"
              className="mx-auto w-32 h-32 object-contain"
            />
          </div>
          <h2 className="text-2xl font-semibold text-red-600 mb-4">License Issue</h2>
          <p className="text-gray-700 text-lg">
            {licenseError || 'Your license has expired. Please contact your Corgee administrator.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Banner when viewing as another user */}
      {isViewingAsOtherUser && (
        <div className="px-4 py-1.5 border-b border-gray-300" style={{ backgroundColor: '#e5e4e4' }} role="alert">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-700">
              <span className="material-icons text-base text-gray-500">person_outline</span>
              <span className="text-gray-600 text-xs">
                Viewing as <strong className="font-medium text-gray-800">{viewingUserName || `User ${viewAsPersonId}`}</strong>
              </span>
            </div>
            <a
              href="/home"
              className="text-[#5dade2] hover:text-[#4a9dce] flex items-center gap-1 transition-colors font-medium text-xs no-underline hover:no-underline"
              style={{ textDecoration: 'none' }}
            >
              <span className="material-icons text-base">arrow_back</span>
              <span>Return to my home</span>
            </a>
          </div>
        </div>
      )}

      {/* Home Header with Profile */}
      <HomeHeader
        viewAsPersonId={isViewingAsOtherUser ? viewAsPersonId : undefined}
        onUserLoaded={handleUserLoaded}
      />

      {/* Tab Navigation */}
      <TabNavigator activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Filter Bar - Only visible in NewsFeed */}
      {activeTab === 'newsfeed' && (
        <section className="flex items-center justify-between py-[5px] pr-2 pb-0 pl-[15px] w-full h-8 bg-transparent">
          <div className="flex items-center gap-2">
            <FilterBar
              filter={filter}
              onChangeFilter={setFilter}
              loading={filterLoading}
              filterOptions={filterOptions}
              error={filterError}
              filtersSelected={filtersSelected}
              onFilterOpened={handleFilterOpened}
              onFilterSelect={handleFilterSelect}
              onFilterUnselect={handleFilterUnselect}
              onApplyFilters={handleApplyFilters}
            />
            <SearchBar filterSearchCard={searchText} onChangeSearchFilterCards={setSearchText} />
          </div>
          <RefreshNotificationButton
            hasPendingUpdates={hasPendingUpdates}
            pendingCount={pendingCount}
            onClick={triggerRefresh}
          />
        </section>
      )}

      {/* Tab Content - Takes remaining height */}
      <div className="dash-container flex-1 overflow-hidden">
        {activeTab === 'newsfeed' && (
          <NewsFeedTab
            searchText={searchText}
            filters={apiFilters}
            extraFilterParams={extraFilterParams}
            refreshToken={refreshToken}
            viewAsPersonId={viewAsPersonId}
          />
        )}
        {activeTab === 'assets' && <AssetsTab viewAsPersonId={viewAsPersonId} />}
        {activeTab === 'efficiency' && <EfficiencyDataTab />}
      </div>
    </>
  );
};

// Keep legacy export for backwards compatibility
export const HomeViewPage = HomeViewContent;
